import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import RoleGuard from "@/components/RoleGuard";
import { NestedItem, SaltShellNavigation } from "@/components/dashboard/salt-shell-navigation";
import { SaltPageChrome } from "@/components/dashboard/salt-page-chrome";
import {
  PageTitleOverrideProvider,
  usePageTitleController,
} from "@/components/dashboard/page-title-override";
import { Drawer, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { useAuth } from "@/hooks/useAuth";
import { useCorporateAccess } from "@/hooks/useCorporateAccess";
import { corporatesService } from "@/services/corporates";
import {
  adminNavigation,
  buyerOnboardingNavigation,
  getActiveTab,
  getPageTitle,
  outreachNavigation,
  primaryNavigation,
  supplierOptimizationNavigation,
  workspaceNavigation,
  type NavItemData,
} from "./dashboard/navigation-config";
import {
  NavigationLevelProvider,
  ParentLevelBackControl,
} from "@/components/dashboard/navigation-level";
import { BorderLayout, BorderItem, StackLayout } from "@/design-system/salt";

interface DashboardLayoutProps {
  children: React.ReactNode;
}

/** Where the chrome's "Create corporate" goes — and the one route it must not offer it on. */
const CREATE_CORPORATE_PATH = "/corporates/create";

function getWorkspaceInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "BA";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

/** Authenticated B2B Activate shell backed by Salt navigation and responsive density. */
export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const { isPortalUser } = useCorporateAccess();
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [chromeSearch, setChromeSearch] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const params = useParams<{ id?: string }>();

  const handleSignOut = async () => {
    await signOut();
    navigate("/auth");
  };

  const corporateId = useMemo(
    () => (params.id ? Number.parseInt(params.id, 10) || 0 : 0),
    [params.id],
  );

  const { data: corporate } = useQuery({
    queryKey: ["corporate", corporateId],
    queryFn: () => corporatesService.getById(corporateId),
    enabled: corporateId > 0,
  });

  useEffect(() => {
    if (location.pathname === "/corporates") {
      setChromeSearch(new URLSearchParams(location.search).get("search") ?? "");
      return;
    }
    setChromeSearch("");
  }, [location.pathname, location.search]);

  const handleChromeSearchSubmit = useCallback(
    (value: string) => {
      const query = value.trim();
      navigate(query ? `/corporates?search=${encodeURIComponent(query)}` : "/corporates");
    },
    [navigate],
  );

  const activeTab = useMemo(() => getActiveTab(location.pathname), [location.pathname]);
  // A page may publish a title only it knows — a vendor's name, a campaign's name. The
  // route table is the answer for every route that does not, which is most of them.
  const { override: pageTitleOverride, setOverride: setPageTitleOverride } =
    usePageTitleController();
  const routeTitle = useMemo(
    () => getPageTitle(location.pathname, corporate?.name),
    [location.pathname, corporate?.name],
  );
  const pageTitle = pageTitleOverride ?? routeTitle;
  const workspaceTitle = corporate?.name || "B2B Activate";
  const workspaceSubtitle = corporate?.name ? "Corporate workspace" : "Campaign engine";
  const workspaceInitials = useMemo(() => getWorkspaceInitials(workspaceTitle), [workspaceTitle]);

  void activeTab;

  const closeNav = useCallback(() => setNavigationOpen(false), []);

  const renderNavItem = useCallback(
    (item: NavItemData) => <NestedItem item={item} key={item.id} onNavigate={closeNav} />,
    [closeNav],
  );

  const renderGuardedNavItem = useCallback(
    (item: NavItemData) => {
      const navItem = renderNavItem(item);
      if (item.id === "admin") {
        return (
          <RoleGuard allowedRoles={["admin"]} key={item.id}>
            {navItem}
          </RoleGuard>
        );
      }
      if (item.id === "intelligence") {
        return (
          <RoleGuard allowedRoles={["admin", "manager"]} key={item.id}>
            {navItem}
          </RoleGuard>
        );
      }
      return navItem;
    },
    [renderNavItem],
  );

  const allNavItems: readonly NavItemData[] = useMemo(
    () => [
      ...primaryNavigation,
      ...workspaceNavigation,
      ...buyerOnboardingNavigation,
      ...outreachNavigation,
      ...supplierOptimizationNavigation,
      ...adminNavigation,
    ],
    [],
  );

  const shellNavigation = (
    <SaltShellNavigation
      homePath={corporateId > 0 ? `/corporate/${corporateId}` : "/corporates"}
      initials={workspaceInitials}
      navItems={allNavItems}
      onNavigate={closeNav}
      onSignOut={handleSignOut}
      subtitle={workspaceSubtitle}
      title={workspaceTitle}
    />
  );

  if (isPortalUser) {
    return (
      <BorderLayout
        gap={0}
        style={{ minHeight: "100vh", background: "var(--salt-container-secondary-background)" }}
      >
        <BorderItem position="north" sticky>
          <SaltPageChrome
            onMenu={() => setNavigationOpen(true)}
            onSearchChange={setChromeSearch}
            onSearchSubmit={handleChromeSearchSubmit}
            searchValue={chromeSearch}
            showSearch={false}
            title={pageTitle}
          />
        </BorderItem>
        <Drawer
          style={{ padding: "0px" }}
          onOpenChange={setNavigationOpen}
          open={navigationOpen}
          position="left"
        >
          <DrawerHeader className="sr-only">
            <DrawerTitle>Account</DrawerTitle>
            <DrawerDescription>Security settings and sign out.</DrawerDescription>
          </DrawerHeader>
          {shellNavigation}
        </Drawer>
        {/* `minWidth: 0` is load-bearing. BorderLayout is a CSS grid whose centre
            track is `1fr`, and a `1fr` track's automatic minimum is content-based,
            so any descendant wider than the phone (a 980px table, a kanban row)
            sizes the SHELL instead of scrolling inside its own container. Measured
            at 390x844 against the installed salt-core.css: 1028px document width
            without it, 390px with it. `maxWidth: 100%` below cannot substitute —
            100% of an already-overgrown column is still overgrown. */}
        <BorderItem position="center" style={{ minWidth: 0 }}>
          {/* The StackLayout is what makes the `flex` below mean anything, and it is
              why the non-portal branch below is built the same way. BorderItem
              renders a Salt GridItem — a plain div whose only declaration is
              `--gridItem-gridArea` — so it establishes NO flex formatting context
              and a `flex` shorthand on a direct child is dropped by the engine.
              Measured against the installed salt-core.css: without the wrapper the
              main's computed parent display is `block` and `flex: 1 1 0%` is inert;
              with it the parent is `flex` and the declaration is honoured. Keep the
              two branches structurally identical so a height constraint added to the
              shell takes effect on the portal route too, not only on the other one. */}
          <StackLayout gap={0}>
            <main style={{ flex: "1 1 0%", overflow: "auto" }}>
              <div className="spacing-page pb-safe" style={{ maxWidth: "100%" }}>
                <PageTitleOverrideProvider value={setPageTitleOverride}>
                  {children}
                </PageTitleOverrideProvider>
              </div>
            </main>
          </StackLayout>
        </BorderItem>
      </BorderLayout>
    );
  }

  return (
    <BorderLayout
      gap={0}
      style={{ minHeight: "100vh", background: "var(--salt-container-secondary-background)" }}
    >
      {/* Must be a BorderItem: BorderLayout places children by grid area, so a raw
          <aside> lands in an implicit row below the content instead of the west
          column — it renders off-screen rather than beside the page. */}
      {/* `sticky` for the same reason the header above carries it. This shell is
          `minHeight: 100vh` — an INDEFINITE height — so a page taller than the
          viewport scrolls the DOCUMENT, not an inner container. The header
          survives that because its BorderItem is declared sticky; the sidebar is
          the same kind of fixed chrome and was missing the prop, so its 100vh box
          scrolled off the top with the page (reported on /corporate/440/settings
          with "Reports" clipped). IssuerHubLayout hit the same class of bug at its
          own shell and documents it there.

          Salt's prop, not a hand-rolled override — BorderItem.css.js emits
          `.saltBorderItem-sticky { position: sticky; top: 0; overflow: auto }`,
          which is exactly what is wanted here, including the overflow (the nav
          scrolls within its own pinned column when it is taller than the
          viewport). */}
      <BorderItem
        as="aside"
        className="app-shell-sidebar"
        position="west"
        sticky
        style={{
          width: 250,
          height: "100vh",
          borderRight: "var(--salt-size-border) solid var(--salt-separable-primary-borderColor)",
        }}
      >
        {shellNavigation}
      </BorderItem>

      <Drawer
        style={{ padding: "0px" }}
        onOpenChange={setNavigationOpen}
        open={navigationOpen}
        position="left"
      >
        <DrawerHeader className="sr-only">
          <DrawerTitle>Application navigation</DrawerTitle>
          <DrawerDescription>Navigate the B2B Activate workspace.</DrawerDescription>
        </DrawerHeader>
        {shellNavigation}
      </Drawer>

      {/* See the portal branch above — `minWidth: 0` is what keeps a wide table
          scrolling inside `.saltTable-container` instead of widening the page. */}
      <BorderItem position="center" style={{ minWidth: 0 }}>
        {/*
          DR-33. The provider renders no DOM of its own — it is a context — so the chrome,
          the back control and <main> stay direct children of this StackLayout and the
          layout is unchanged. It has to wrap BOTH the back control and <main> because the
          tab rows that register with it live inside the page, while the affordance that
          replaces them lives in the chrome.
        */}
        <NavigationLevelProvider>
        <StackLayout gap={0}>
          <SaltPageChrome
            onMenu={() => setNavigationOpen(true)}
            onSearchChange={setChromeSearch}
            onSearchSubmit={handleChromeSearchSubmit}
            /*
              The chrome search is "Search all corporates" — a LIST affordance. On a
              create form there is no list to narrow, so it was furniture competing
              with the form's own first field.

              MEASURED at 390x844 on /corporates/create before this: an 89px header
              whose row wrapped, carrying a search box the page has no use for.
            */
            showSearch={
              location.pathname !== CREATE_CORPORATE_PATH && location.pathname !== "/corporates"
            }
            searchPlaceholder="Search all corporates"
            searchValue={chromeSearch}
            title={pageTitle}
          />

          {/*
            The level above the visible tab row, as a real control with an accessible name
            — DR-33 forbids a decorative chevron. Renders nothing on desktop and nothing on
            a route with a single tab row, so it costs no height where it earns none.
          */}
          <ParentLevelBackControl />

          <main
            style={{
              flex: "1 1 0%",
              overflow: "auto",
              background: "var(--salt-container-secondary-background)",
            }}
          >
            {/* The page gutter is a PROP, not a class. `.spacing-page` used to be
                here and rendered nothing: it is declared in `@layer utilities`,
                Salt's `.saltFlexLayout { padding: var(--flexLayout-padding) }` is
                unlayered, and unlayered beats layered at any specificity — while
                StackLayout always writes an inline `--flexLayout-padding` (padding
                defaults to 0, not to unset). Result was a zero gutter on every
                authenticated route. `padding` takes a string verbatim, so the
                density-aware page-margin token goes straight through.

                The safe-area inset has to be an INLINE style for the same reason:
                `.pb-safe` is unlayered and only wins by stylesheet order. Inline
                NORMAL beats every NORMAL stylesheet rule, layered or unlayered —
                it does NOT beat an `!important` one. MEASURED 2026-08-17 on a
                real `.saltFlexLayout`: inline `gap: 61px` lost to a layered
                `!important` 62px, and only inline `!important` won it back. So
                this inset is safe because nothing declares `padding-bottom
                !important` in a layer, not because inline outranks everything.
                See DR-28's measured precedence table. */}
            {/* The seam below is the space BETWEEN a page's top-level children, and it
                is the shell's call, not the page's. (It was `gap={3}` until the note
                after this one; the sizing moved, the reasoning did not.)
                A page that returns a fragment —
                a tab bar or a return-to-setup banner beside its body — lands both
                nodes here as siblings; at `gap={0}` they touched, while the sibling
                pages that happen to nest the same tab bar one level deeper inside
                their own `gap={3}` StackLayout got 24px. Measured on the running
                app: /corporate/24/vendors/create read 0px between the tab bar and
                the form, /corporate/24/vendors read 24px, from identical markup.
                Inert on the ~70 pages that return a single element — gap only
                applies between children. */}
            {/* The seam is set from Salt's CURATED token, not from `gap={3}`, because at
                mobile density those two are not the same number.

                `.salt-density-mobile` in `@salt-ds/theme` declares a FLAT scale —
                100:16, 150:18, 200:20, 300:24 — growing ~2px per half-step so that large
                spacings do not blow up on a phone. `--salt-spacing-300` is 24px there,
                deliberately the SAME as medium. Every other density derives its steps
                multiplicatively from `--salt-spacing-100`.

                `StackLayout gap={N}` ignores that and emits
                `calc(var(--salt-spacing-100) * N)`. At medium it coincides with the
                curated token (3 x 8 = 24); at mobile it does not (3 x 16 = 48). So the
                multiplier form silently doubles the seam on exactly the screen with the
                least room, and the divergence grows with N — gap={2} is 1.6x, gap={3} is
                2.0x, gap={4} is 2.3x.

                MEASURED in Chrome at 390x844 with a coarse pointer (`salt-density-mobile`,
                `--salt-size-base: 44px`) on /corporate/440/vendors/create — the shell
                route whose page returns two top-level children, so this seam is the one
                separating them. Before: tab row 156-216, form at 264, first card 356.
                After: form at 240, first card 332. 24px recovered, and the seam now reads
                the same 24px Salt's own mobile scale specifies.

                It has to be a CLASS carrying `!important`, and that is not belt-and-braces.
                `gap` has no token form, and passing the custom property through `style`
                DOES NOT WORK: StackLayout spreads the caller's style FIRST and then writes
                its own `--flexLayout-gap`, so the key keeps the caller's insertion position
                while carrying Salt's value. MEASURED — the rendered attribute after trying
                exactly that was
                  `--flexLayout-gap: calc(var(--salt-spacing-100) * 3); min-height: 100%; ...`
                with the caller's `var(--salt-spacing-300)` gone entirely and computed `gap`
                still 48px. A caller cannot outrank Salt inline, at any density.

                So the override moves to `.app-shell-page-stack` in index.css, where
                `!important` wins: important-author beats normal-inline in the cascade.
                This is the same precedence DR-28 measured on a real `.saltFlexLayout` and
                which the `padding` note above depends on, read the other way round.

                Guarded by `src/test/salt-gap-mobile-scale.test.ts`, which also pins the
                Salt theme premise — if Salt ever makes the mobile scale multiplicative,
                that test fails and this can go back to a plain `gap` prop. */}
            <StackLayout
              className="app-shell-page-stack"
              padding="var(--salt-layout-page-margin)"
              style={{
                minHeight: "100%",
                maxWidth: "100%",
                paddingBottom:
                  "calc(var(--salt-layout-page-margin) + env(safe-area-inset-bottom, 0px))",
              }}
            >
              <PageTitleOverrideProvider value={setPageTitleOverride}>
                {children}
              </PageTitleOverrideProvider>
            </StackLayout>
          </main>
        </StackLayout>
        </NavigationLevelProvider>
      </BorderItem>
    </BorderLayout>
  );
}
